<template>
  <div class="bg-white shadow rounded-lg p-6 w-full">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div class="text-left">
        <h3 class="text-lg font-medium text-gray-900">{{ title }}</h3>
        <p class="text-sm text-gray-500">{{ description }}</p>
      </div>
      <div class="flex gap-2">
        <button
          type="button"
          class="button-cancel disabled:opacity-50"
          :disabled="busy || !queues.length || allPaused"
          @click="$emit('pause-all')"
        >
          Pause all
        </button>
        <button
          type="button"
          class="button-submit"
          :disabled="busy || !queues.length || nonePaused"
          @click="$emit('resume-all')"
        >
          Resume all
        </button>
      </div>
    </div>

    <p v-if="!queues.length" class="mt-6 text-sm text-gray-500">
      No queues registered.
    </p>
    <div v-else class="mt-6 overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200 text-sm">
        <thead>
          <tr class="text-left text-gray-500">
            <th class="py-2 pr-4 font-medium">Queue</th>
            <th v-if="showCounts" class="py-2 pr-4 font-medium">Jobs</th>
            <th class="py-2 pr-4 font-medium">Status</th>
            <th class="py-2 font-medium text-right">Actions</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          <tr v-for="queue in queues" :key="queue.name">
            <td class="py-2 pr-4 text-left">
              <p class="font-mono">{{ queue.name }}</p>
              <p class="text-xs text-gray-500">{{ queue.description }}</p>
            </td>
            <td
              v-if="showCounts"
              class="py-2 pr-4 text-left text-gray-700 whitespace-nowrap"
            >
              {{ formatCounts(queue.counts) }}
            </td>
            <td class="py-2 pr-4 text-left">
              <span
                :class="[
                  'px-2 rounded-full',
                  queue[pausedKey]
                    ? 'text-yellow-800 bg-yellow-100'
                    : 'text-green-800 bg-green-100',
                ]"
              >
                {{ queue[pausedKey] ? "Paused" : "Active" }}
              </span>
            </td>
            <td class="py-2 text-right whitespace-nowrap">
              <button
                v-if="queue[pausedKey]"
                type="button"
                class="text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
                :disabled="busy"
                @click="$emit('resume', queue)"
              >
                Resume
              </button>
              <button
                v-else
                type="button"
                class="text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
                :disabled="busy"
                @click="$emit('pause', queue)"
              >
                Pause
              </button>
              <!-- Only the owner of a queue may obliterate it; the backend
                   refuses otherwise, so don't offer it. -->
              <button
                v-if="canObliterate && queue.owned"
                type="button"
                class="ml-4 text-red-600 hover:text-red-800 disabled:opacity-50"
                :disabled="busy"
                @click="$emit('obliterate', queue)"
              >
                Obliterate
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: "QueuesTable",
  props: {
    title: { type: String, required: true },
    description: { type: String, default: "" },
    queues: { type: Array, default: () => [] },
    // The flag that says this table's action is paused: "paused" for
    // consumption, "producerPaused" for producing.
    pausedKey: { type: String, default: "paused" },
    showCounts: { type: Boolean, default: false },
    canObliterate: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
  },
  emits: ["pause", "resume", "pause-all", "resume-all", "obliterate"],
  computed: {
    allPaused() {
      return this.queues.every((queue) => queue[this.pausedKey]);
    },
    nonePaused() {
      return this.queues.every((queue) => !queue[this.pausedKey]);
    },
  },
  methods: {
    formatCounts(counts) {
      if (!counts) return "—";
      return `${counts.waiting || 0} waiting · ${counts.active || 0} active · ${
        counts.failed || 0
      } failed`;
    },
  },
};
</script>
